// Mirrors the API contract — types kept aligned with mobile/services/api.ts
// and src/Jotly.Api/Models/ApiContracts.cs. Duplication here (vs a shared
// package) is deliberate at this scale: copy is cheaper than a workspace.

export interface TaskItem {
  pk: string;
  sk: string;
  title: string;
  rawText: string;
  notes: string | null;
  tags: string[] | null;
  category: string;
  context: string;
  track: string;
  status: string;
  dueDate: string | null;
  reminderAt: string | null;
  sourcePerson: string;
  highPriority: boolean;
  captureMethod: string;
  sourceNote: string | null;
  relayTarget: string | null;
  relayAfter: string | null;
  suggestDraft: boolean;
  bringUpWith: string[] | null;
  projectId: string | null;
  priority: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ShoppingItem {
  id: string;
  name: string;
  qty: number;
  checked: boolean;
  addedAt: string;
}

export interface ShoppingList {
  pk: string;
  sk: string;
  store: string;
  items: ShoppingItem[];
  updatedAt: string;
}

export interface ProjectItem {
  pk: string;
  sk: string;
  name: string;
  status: string;
  createdAt: string;
}

export interface PersonBucket {
  name: string;     // lowercase kebab — e.g. "phil"
  label: string;    // display form — e.g. "Phil"
  openCount: number;
}

export interface AssistantReply {
  message: string;
  messageType: string | null;
  tasks?: TaskItem[] | null;
  list?: ShoppingList | null;
  projects?: ProjectItem[] | null;
  people?: PersonBucket[] | null;
  taskSk?: string | null;
  draft?: string | null;
  ruleHandled?: boolean;
}

export class NotAuthenticatedError extends Error {
  constructor() {
    super('Not authenticated');
    this.name = 'NotAuthenticatedError';
  }
}

// Thrown when the API returns 403 with `{ error: "ACCOUNT_BLOCKED" }`.
// Top-level error boundary renders the suspended-account screen rather than
// retrying the request.
export class AccountBlockedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AccountBlockedError';
  }
}

// ─── M8 Notes ────────────────────────────────────────────────────────────────

export interface NoteResponse {
  id: string;                              // ULID, prefix-stripped
  title: string;
  bodyMarkdown: string;
  tags: string[];                          // empty until enricher ships
  summary: string | null;
  wordCount: number;
  track: string | null;
  pinnedAt: string | null;
  archivedAt: string | null;
  purgedAt: string | null;
  enrichmentStatus: 'pending' | 'complete' | 'failed';
  createdAt: string;
  updatedAt: string;
}

export interface NoteListResponse {
  notes: NoteResponse[];
}

export interface CreateNoteRequest {
  title?: string;
  bodyMarkdown: string;
  track?: string;
}

export interface UpdateNoteRequest {
  title?: string;
  bodyMarkdown: string;
  track?: string;
}
